import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  type Ref,
} from 'react';

import { CalendarHeader } from './calendar-header/CalendarHeader.js';
import { CALENDAR_ERROR_MESSAGES } from './calendarMessages.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useCalendarStore } from '../../core/time/timeframeStore/useTimeframeStore.js';
import { validate } from '../../core/time/validation.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import type { FormControlRef, TimeRangePickerRef } from '../shared-types.js';
import * as styles from './Calendar.sty.js';
import type {
  CalendarStrictValueType,
  CalendarValueType,
} from './Calendar.types.js';
import { CalendarContext } from './contexts/CalendarContext.js';
import { UseCalendarProps, useCalendar } from './hooks/useCalendar.js';
import { useCalendarNavigation } from './hooks/useCalendarNavigation.js';
import type { CalendarViewType } from './types/shared-types.js';
import { SelectDayView } from './views/SelectDayView.js';
import { SelectMonthView } from './views/SelectMonthView.js';
import { SelectYearView } from './views/SelectYearView.js';
import { isFullTimeframe, isTimeValue } from '../../core/time/helpers.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';

const COMPONENT_NAME = 'Calendar';

//#region types
/**
 * @internal
 */
export type CalendarType = 'single' | 'range';

/** @internal */
export type CalendarProps<Type extends CalendarType> = FormControlProps<
  CalendarValueType<Type>,
  (date: CalendarStrictValueType<Type>) => void
> &
  StylingProps &
  MaskingProps &
  DataTestId & {
    /** The earliest selectable date. It will always consider the start of the day as time. */
    min?: string;
    /** The latest selectable date. It will always consider the end of the day as time. */
    max?: string;
    /** Determines the calendar type. Single allows the selection of one date and range allows a range selection with a from and to value.
     * @defaultValue 'single'
     */
    type?: Type;
    /**
     * The precision of the time shown in the display value.
     * @defaultValue 'minutes'
     */
    precision?: 'minutes' | 'seconds' | 'milliseconds';
  };
//#endregion

/** Render the correct view depending on the currentView state. */
function renderView(view: CalendarViewType) {
  switch (view) {
    case 'month':
      return <SelectMonthView />;
    case 'year':
      return <SelectYearView />;
    default:
      return <SelectDayView />;
  }
}

function CalendarComponent<Type extends CalendarType = 'single'>(
  props: Omit<CalendarProps<Type>, 'value' | 'defaultValue' | 'onChange'>,
  forwardedRef: Ref<
    | FormControlRef<HTMLDivElement>
    | (Omit<FormControlRef<HTMLDivElement>, 'inputRef'> &
        Pick<TimeRangePickerRef<HTMLDivElement>, 'inputRef'>)
  >,
) {
  const {
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    id,
    disabled,
    'aria-disabled': ariaDisabled,
    required,
    name,
    readOnly = false,
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
    // @ts-ignore Strict mode transition - needs to be investigated
    type = 'single',
    'aria-label': ariaLabelProp,
    'aria-labelledby': ariaLabelledByProp,
    'aria-describedby': ariaDescribedByProp,
    'aria-details': ariaDetailsProp,
    ...remainingProps
  } = props;

  const calendarValue = useCalendarStore((state) => state.calendarValue);
  const min = useCalendarStore((state) => state.min);
  const max = useCalendarStore((state) => state.max);
  const error = useCalendarStore((state) => state.calendarError);
  const precision = useCalendarStore((state) => state.precision);

  // Imperative Handle Construction
  const calendarRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLInputElement>(null);
  const refTo = useRef<HTMLInputElement>(null);

  const { uniqueId, state, dispatch } = useCalendar<Type>({
    type,
  } as UseCalendarProps<Type>);

  useCalendarNavigation({
    calendarRef,
    view: state.currentView,
    dispatch,
  });

  const { dispatch: formMessageDispatch } = useFormFieldMessagesContext();

  const refValidate = useCallback(() => {
    formMessageDispatch?.({
      type: FormFieldMessagesAction.SETISTOUCHED,
      payload: { isTouched: true },
    });

    validate(calendarValue, min, max, {
      formMessageDispatch,
      formerError: error,
      precision,
      showError: true,
      checkOrder: false,
      ref,
      errorMessages: CALENDAR_ERROR_MESSAGES,
    });
  }, [min, max, formMessageDispatch, error, precision, ref, calendarValue]);

  useImperativeHandle(
    forwardedRef,
    ():
      | FormControlRef<HTMLDivElement>
      | (Omit<FormControlRef<HTMLDivElement>, 'inputRef'> &
          Pick<TimeRangePickerRef<HTMLDivElement>, 'inputRef'>) => {
      if (type === 'single') {
        return {
          element: calendarRef.current,
          inputRef: ref.current,
          validate: refValidate,
        };
      }

      return {
        element: calendarRef.current,
        inputRef: {
          from: ref?.current,
          to: refTo?.current,
        },
        validate: refValidate,
      };
    },
    [refValidate, type],
  );

  const {
    ariaProps,
    onChange: validationOnChange,
    onBlur,
    onInvalid,
  } = useFormFieldValidation(
    ref,
    {},
    {
      'aria-label': ariaLabelProp,
      'aria-labelledby': ariaLabelledByProp,
      'aria-describedby': ariaDescribedByProp,
      'aria-details': ariaDetailsProp,
    },
  );

  const labelRef = useRef<string>(`calendar-body-label-${uniqueId}`);

  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const getNativeProps = (suffix = '') => ({
    readOnly: readOnly,
    required,
    disabled,
    'aria-disabled': ariaDisabled,
    name: name && `${name}${suffix}`,
    id,
  });

  const calenderContextValue = useMemo(
    () => ({ state, dispatch, readOnly }),
    [state, dispatch, readOnly],
  );

  return (
    <CalendarContext.Provider value={calenderContextValue}>
      <div
        className={clsx(styles.calendar({ readOnly }), consumerClassName, {
          [styles.disabled]: disabledOrAriaDisabled,
        })}
        data-testid={dataTestId}
        ref={calendarRef}
        style={consumerStyle}
        {...remainingProps}
        data-dt-component={COMPONENT_NAME}
      >
        <CalendarHeader view={state.currentView} labelId={labelRef.current} />
        <div aria-labelledby={labelRef.current}>
          <table
            role="grid"
            className={styles.body({
              isReadOnly: !!readOnly,
            })}
          >
            {renderView(state.currentView)}
          </table>
        </div>
      </div>
      <VisuallyHidden>
        {isTimeValue(calendarValue) ? (
          <input
            onChange={validationOnChange}
            onBlur={onBlur}
            onInvalid={onInvalid}
            {...ariaProps}
            {...getNativeProps()}
            ref={ref}
            value={calendarValue?.absoluteDate ?? ''}
            tabIndex={-1}
          />
        ) : (
          <>
            <input
              onChange={validationOnChange}
              onBlur={onBlur}
              onInvalid={onInvalid}
              {...ariaProps}
              {...getNativeProps('-from')}
              ref={ref}
              // Only set if a complete timeframe is available to rely on the browser validation for required.
              value={
                isFullTimeframe(calendarValue) && calendarValue?.from?.value
                  ? calendarValue.from.value
                  : ''
              }
              tabIndex={-1}
            />
            {/* This input is ONLY used for form submission and not for validation. */}
            <input
              {...getNativeProps('-to')}
              ref={refTo}
              value={calendarValue?.to?.value ?? ''}
              id={`${id}-to`}
              type="hidden"
            />
          </>
        )}
      </VisuallyHidden>
    </CalendarContext.Provider>
  );
}

CalendarComponent['displayName'] = COMPONENT_NAME;

/**
 * Calendar component
 * Renders the calendar header and body depending on the current view
 * @internal
 */
export const CalendarRoot = forwardRef(CalendarComponent);
